import { getActivation } from '@/_shared/components/activations/registry';
import { Card, CardContent, CardHeader, CardTitle } from '@/_shared/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/_shared/components/ui/tabs';

import type { RenderedPreview } from '../types';

interface DryRunData {
  sample: Record<string, unknown>[];
  renderedPreview?: RenderedPreview;
}

export function DryRunResults({ data, actionType }: { data: DryRunData; actionType: string }): React.ReactElement {
  const activation = getActivation(actionType);

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm">Dry Run Results</CardTitle>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="results">
          <TabsList>
            <TabsTrigger value="results">Results</TabsTrigger>
            <TabsTrigger value="preview">Preview</TabsTrigger>
          </TabsList>
          <TabsContent value="results">
            {data.sample.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b text-left text-muted-foreground">
                      {Object.keys(data.sample[0]!).map((col) => (
                        <th key={col} className="pr-4 pb-2 font-medium">
                          {col}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {data.sample.slice(0, 10).map((row, i) => (
                      <tr key={i} className="border-b border-border/50 last:border-0">
                        {Object.values(row).map((val, j) => (
                          <td key={j} className="py-1.5 pr-4 font-mono">
                            {val != null ? String(val as string | number | boolean) : '\u2014'}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="py-4 text-center text-xs text-muted-foreground">No rows returned.</p>
            )}
          </TabsContent>
          <TabsContent value="preview">
            {data.renderedPreview && data.renderedPreview.messages.length > 0 ? (
              <div className="space-y-3">
                {data.renderedPreview.messages.map((msg, i) => {
                  const PreviewComponent = activation?.PreviewMessage;
                  return PreviewComponent ? (
                    <PreviewComponent key={i} message={msg} />
                  ) : (
                    <pre key={i} className="rounded-md bg-muted p-2 text-xs">
                      {JSON.stringify(msg, null, 2)}
                    </pre>
                  );
                })}
                <p className="text-xs text-muted-foreground">
                  Showing {data.renderedPreview.messages.length} of {data.renderedPreview.totalCount} messages
                </p>
              </div>
            ) : (
              <p className="py-4 text-center text-xs text-muted-foreground">No preview messages generated.</p>
            )}
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}
